import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api.js';
import { Doodle, Reveal } from '../Doodles.jsx';

const MAX_FILES = 6;
const MAX_MB = 40;

const TIPOS = ['Tarta personalizada', 'Cookie cake', 'Cheesecake', 'Caja / mesa dulce', 'Otro'];
const OCASIONES = ['Cumpleaños', 'Boda', 'Comunión / bautizo', 'Aniversario', 'Baby shower', 'Empresa / evento', 'Otra'];

export default function PedidoPersonalizado() {
  const [f, setF] = useState({
    tipoDulce: 'Tarta personalizada', ocasion: 'Cumpleaños', personas: '', fechaEvento: '',
    descripcion: '', tematica: '', alergenos: '', presupuesto: '',
    tipoEntrega: 'recogida', direccion: '', referencia: '',
    nombre: '', telefono: '', email: '', privacidad: false,
  });
  const [err, setErr] = useState('');
  const [sending, setSending] = useState(false);
  const [ok, setOk] = useState(null);
  const [files, setFiles] = useState([]);
  const fileInput = useRef(null);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const onFiles = (e) => {
    setErr('');
    const incoming = Array.from(e.target.files || []);
    const next = [...files];
    for (const file of incoming) {
      if (next.length >= MAX_FILES) { setErr(`Máximo ${MAX_FILES} archivos.`); break; }
      if (file.size > MAX_MB * 1024 * 1024) { setErr(`"${file.name}" supera ${MAX_MB} MB.`); continue; }
      if (!/^(image|video)\//.test(file.type)) { setErr('Solo se admiten fotos o vídeos.'); continue; }
      next.push(file);
    }
    setFiles(next.slice(0, MAX_FILES));
    if (fileInput.current) fileInput.current.value = '';
  };
  const removeFile = (i) => setFiles((prev) => prev.filter((_, k) => k !== i));
  const hoy = new Date(); hoy.setDate(hoy.getDate() + 3);
  const minDate = hoy.toISOString().slice(0, 10);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!f.nombre || !f.telefono) return setErr('Necesitamos tu nombre y teléfono para contactarte.');
    if (f.descripcion.trim().length < 10) return setErr('Cuéntanos un poco más tu idea.');
    if (!f.privacidad) return setErr('Debes aceptar la política de privacidad.');
    setSending(true);
    try {
      const fd = new FormData();
      const fields = {
        nombre: f.nombre, telefono: f.telefono, email: f.email,
        tipoDulce: f.tipoDulce, ocasion: f.ocasion, personas: f.personas,
        fechaEvento: f.fechaEvento || '', descripcion: f.descripcion,
        tematica: f.tematica, alergenos: f.alergenos, presupuesto: f.presupuesto,
        tipoEntrega: f.tipoEntrega, direccion: f.direccion, referencia: f.referencia,
        privacidad: f.privacidad ? 'true' : 'false',
      };
      Object.entries(fields).forEach(([k, v]) => fd.append(k, v));
      files.forEach((file) => fd.append('files', file));
      const res = await api.createCustomOrder(fd);
      setOk(res.codigo);
      window.scrollTo(0, 0);
    } catch (e2) { setErr(e2.message); }
    finally { setSending(false); }
  };

  if (ok) {
    return (
      <div className="page narrow">
        <div className="confirm">
          <div className="confirm-icon"><Doodle name="heart" size={48} draw color="var(--rose)" /></div>
          <h1>¡Idea recibida!</h1>
          <p className="prose">Gracias por confiarnos tu celebración. Tu solicitud es la <b>{ok}</b>.
            La revisamos con mimo y te contactamos muy pronto por WhatsApp o teléfono para darle forma y presupuesto.</p>
          <Link to="/" className="btn btn--primary">Volver al inicio</Link>
          <Link to="/carta" className="btn btn--ghost">Ver la carta</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page narrow">
      <div className="page-head">
        <p className="kicker hand" style={{ justifyContent: 'center', fontSize: '1.3rem' }}>
          <Doodle name="sparkle" size={26} color="var(--mint-d)" /> Algo único, hecho para ti
        </p>
        <h1>Pedido personalizado</h1>
        <p>¿Tienes una idea en mente? Cuéntanosla y la hacemos realidad: tartas a medida, diseños temáticos,
          fotos comestibles, mesas dulces… Te respondemos con propuesta y presupuesto.</p>
      </div>

      <Reveal now>
      <form className="checkout-form custom-form" onSubmit={submit}>
        <h3>¿Qué te imaginas?</h3>
        <div className="cf-row">
          <label>Tipo de dulce
            <select value={f.tipoDulce} onChange={set('tipoDulce')}>{TIPOS.map((t) => <option key={t}>{t}</option>)}</select>
          </label>
          <label>Ocasión
            <select value={f.ocasion} onChange={set('ocasion')}>{OCASIONES.map((t) => <option key={t}>{t}</option>)}</select>
          </label>
        </div>
        <div className="cf-row">
          <label>Nº de personas / porciones
            <input type="text" value={f.personas} onChange={set('personas')} placeholder="Ej: 20-25" />
          </label>
          <label>Fecha del evento
            <input type="date" min={minDate} value={f.fechaEvento} onChange={set('fechaEvento')} />
          </label>
        </div>
        <label>Cuéntanos tu idea *
          <textarea rows={4} value={f.descripcion} onChange={set('descripcion')}
            placeholder="Describe lo que tienes en mente: sabores, estilo, figuras, texto, inspiración…" />
        </label>
        <div className="cf-row">
          <label>Colores / temática
            <input type="text" value={f.tematica} onChange={set('tematica')} placeholder="Ej: tonos pastel, tema jungla…" />
          </label>
          <label>Alérgenos o restricciones
            <input type="text" value={f.alergenos} onChange={set('alergenos')} placeholder="Ej: sin frutos secos" />
          </label>
        </div>
        <div className="cf-row">
          <label>Presupuesto orientativo
            <input type="text" value={f.presupuesto} onChange={set('presupuesto')} placeholder="Ej: 40-60 €" />
          </label>
          <label>Enlace de referencia (opcional)
            <input type="text" value={f.referencia} onChange={set('referencia')} placeholder="Pega un enlace (Pinterest, Instagram…) o déjalo en blanco" />
          </label>
        </div>

        <label>Fotos o vídeos de referencia (opcional)</label>
        <div className="uploader">
          <button type="button" className="uploader-btn" onClick={() => fileInput.current?.click()} disabled={files.length >= MAX_FILES}>
            <Doodle name="sparkle" size={22} color="var(--mint-d)" />
            <span>{files.length ? 'Añadir más archivos' : 'Subir fotos o vídeos'}</span>
            <small>Hasta {MAX_FILES} archivos · máx. {MAX_MB} MB cada uno</small>
          </button>
          <input ref={fileInput} type="file" accept="image/*,video/*" multiple onChange={onFiles} hidden />
          {files.length > 0 && (
            <div className="upload-list">
              {files.map((file, i) => {
                const isVid = file.type.startsWith('video');
                const url = URL.createObjectURL(file);
                return (
                  <div key={i} className="upload-thumb">
                    {isVid
                      ? <video src={url} muted />
                      : <img src={url} alt={file.name} />}
                    {isVid && <span className="upload-badge">▶</span>}
                    <button type="button" className="upload-del" onClick={() => removeFile(i)} aria-label="Quitar">×</button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <small style={{ display: 'block', marginTop: '8px', marginBottom: '14px', color: 'var(--plum-soft)' }}>
          Sube inspiración, bocetos o un vídeo explicando tu idea. También puedes enviárnoslo por WhatsApp cuando te contactemos 💌
        </small>

        <h3>Entrega</h3>
        <div className="seg">
          <button type="button" className={`seg-b${f.tipoEntrega === 'recogida' ? ' on' : ''}`} onClick={() => setF((p) => ({ ...p, tipoEntrega: 'recogida' }))}>Recogida</button>
          <button type="button" className={`seg-b${f.tipoEntrega === 'envio' ? ' on' : ''}`} onClick={() => setF((p) => ({ ...p, tipoEntrega: 'envio' }))}>Envío</button>
        </div>
        {f.tipoEntrega === 'envio' && (
          <label>Dirección de envío (Madrid)
            <input type="text" value={f.direccion} onChange={set('direccion')} placeholder="Calle, número, CP" />
          </label>
        )}

        <h3>Tus datos</h3>
        <div className="cf-row">
          <label>Nombre *
            <input type="text" value={f.nombre} onChange={set('nombre')} placeholder="Tu nombre" />
          </label>
          <label>Teléfono / WhatsApp *
            <input type="tel" value={f.telefono} onChange={set('telefono')} placeholder="6XX XXX XXX" />
          </label>
        </div>
        <label>Email (opcional)
          <input type="email" value={f.email} onChange={set('email')} placeholder="tu@email.com" />
        </label>
        <label className="check">
          <input type="checkbox" checked={f.privacidad} onChange={set('privacidad')} />
          <span>He leído y acepto la <Link to="/legal" className="linklike">política de privacidad</Link>.</span>
        </label>

        {err && <p className="form-err">{err}</p>}
        <button type="submit" className="btn btn--primary btn--block" disabled={sending}>
          {sending ? 'Enviando…' : 'Enviar mi idea'}
        </button>
        <p className="note"><Doodle name="heart" size={18} color="var(--rose)" /> Sin compromiso: primero te damos propuesta y presupuesto.</p>
      </form>
      </Reveal>
    </div>
  );
}
